/**
 * Where the checklist's label sits in its viewport: the zoom and offset of the
 * page, kept pure so the rules are pinned by tests.
 *
 * Unlike the document view's frame, this viewport is not the page's own size —
 * a page shorter than the viewport is centred in it — so the bounds are
 * worked out from both.
 */

import { clamp } from "../use-pdf-page"

export const ZOOM_FLOOR = 1
export const ZOOM_CEIL = 4

export interface LabelView {
  zoom: number
  tx: number
  ty: number
}

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

interface Sizes {
  /** The viewport, in CSS pixels. */
  vpW: number
  vpH: number
  /** The page at zoom 1, in CSS pixels. */
  stageW: number
  stageH: number
}

/**
 * Keep the page covering the viewport: no blank margin can be panned into
 * view. A page shorter than the viewport, even zoomed, stays centred.
 */
export function clampLabelView(view: LabelView, s: Sizes): LabelView {
  const zoom = clamp(view.zoom, ZOOM_FLOOR, ZOOM_CEIL)
  const scaledW = s.stageW * zoom
  const scaledH = s.stageH * zoom
  return {
    zoom,
    tx: clamp(view.tx, Math.min(0, s.vpW - scaledW), 0),
    ty: scaledH >= s.vpH ? clamp(view.ty, s.vpH - scaledH, 0) : (s.vpH - scaledH) / 2,
  }
}

/** The view that centres `box` at `zoom`, as near the middle as the page allows. */
export function centreOn(box: Box | null, zoom: number, s: Sizes): LabelView {
  if (!box || s.vpW <= 0 || s.vpH <= 0 || s.stageH <= 0) {
    return clampLabelView({ zoom, tx: 0, ty: 0 }, s)
  }
  const cx = (box.x + box.w / 2) * s.stageW
  const cy = (box.y + box.h / 2) * s.stageH
  return clampLabelView({ zoom, tx: s.vpW / 2 - cx * zoom, ty: s.vpH / 2 - cy * zoom }, s)
}

/** Change the zoom, keeping the page point under (`cx`, `cy`) in the viewport where it is. */
export function zoomAround(
  view: LabelView,
  nextZoom: number,
  cx: number,
  cy: number,
  s: Sizes,
): LabelView {
  const zoom = clamp(nextZoom, ZOOM_FLOOR, ZOOM_CEIL)
  const ratio = zoom / view.zoom
  return clampLabelView(
    { zoom, tx: cx - (cx - view.tx) * ratio, ty: cy - (cy - view.ty) * ratio },
    s,
  )
}

/** Change the zoom, keeping the viewport's centre on the same point of the page. */
export function zoomAroundCentre(view: LabelView, nextZoom: number, s: Sizes): LabelView {
  return zoomAround(view, nextZoom, s.vpW / 2, s.vpH / 2, s)
}

/** Move the page by a drag of (`dx`, `dy`) from where it was when the drag began. */
export function panBy(from: LabelView, dx: number, dy: number, s: Sizes): LabelView {
  return clampLabelView({ zoom: from.zoom, tx: from.tx + dx, ty: from.ty + dy }, s)
}
